import { initializeApp, type FirebaseApp } from "firebase/app";
import {
  browserLocalPersistence,
  getAuth,
  onAuthStateChanged,
  setPersistence,
  signInWithCustomToken,
  signOut,
  type Auth,
  type User
} from "firebase/auth";
import { getFirebaseConfig, type RuntimeEnv } from "./config";

export type AuthUser = {
  uid: string;
  displayName: string | null;
  email: string | null;
  getIdToken: () => Promise<string>;
};

export type AuthProviderPort = {
  initialize(): Promise<void>;
  onChange(callback: (user: AuthUser | null) => void): () => void;
  signInWithHandoffToken(firebaseCustomToken: string): Promise<void>;
  signOut(): Promise<void>;
};

function mapUser(user: User): AuthUser {
  return {
    uid: user.uid,
    displayName: user.displayName,
    email: user.email,
    getIdToken: () => user.getIdToken()
  };
}

export function createFirebaseAuthProvider(env: RuntimeEnv): AuthProviderPort {
  const firebaseApp: FirebaseApp = initializeApp(getFirebaseConfig(env), "ltd-accounting");
  const auth: Auth = getAuth(firebaseApp);

  return {
    async initialize() {
      await setPersistence(auth, browserLocalPersistence);
    },
    onChange(callback) {
      return onAuthStateChanged(auth, (user) => callback(user ? mapUser(user) : null));
    },
    async signInWithHandoffToken(firebaseCustomToken) {
      await setPersistence(auth, browserLocalPersistence);
      await signInWithCustomToken(auth, firebaseCustomToken);
    },
    async signOut() {
      await signOut(auth);
    }
  };
}

export function createMockAuthProvider(): AuthProviderPort {
  const user: AuthUser = {
    uid: "dev-owner",
    displayName: "Development Owner",
    email: null,
    getIdToken: async () => "mock-token"
  };

  return {
    async initialize() {
      return undefined;
    },
    onChange(callback) {
      queueMicrotask(() => callback(user));
      return () => undefined;
    },
    async signInWithHandoffToken() {
      return undefined;
    },
    async signOut() {
      return undefined;
    }
  };
}

export function getAuthFailureMessage(error: unknown): string {
  const code = typeof error === "object" && error !== null && "code" in error ? String(error.code) : "";
  if (code === "auth/network-request-failed") {
    return "Не удалось подключиться к Firebase. Проверьте сеть и откройте Бухгалтерию из AGC CRM ещё раз.";
  }
  if (code === "auth/invalid-custom-token" || code === "auth/custom-token-mismatch") {
    return "Сессия Бухгалтерии не подтверждена. Вернитесь в AGC CRM и откройте Бухгалтерию заново.";
  }
  return "Доступ к Бухгалтерии не подтверждён. Вернитесь в AGC CRM и откройте Бухгалтерию заново.";
}
